import DataService from "@/services/DataService";
import Constants from "@/helpers/constants";
import dayjs from "dayjs";
import { StatusCodes } from "http-status-codes";
const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export const namespaced = true;

export const state = {
  usersToday: [],
  dataSleepRanges: [],
  dataSleep: [],
  dataSleepHours: [],
  dataRecoveryRanges: [],
  dataRecovery: [],
  dataAvgRecoveryAndHrv: [],
  dataStrainRanges: [],
  dataStrain: [],
  dataStrainAvgMaxHr: [],
  dataWorkoutTypes: [],
  dataWorkouts: [],
  userDnaProfiles: [],
};

export const mutations = {
  // used for dashboard, users and user physiological data
  setSelectedDate(state, dt) {
    localStorage.setItem("selectedDate", dt);
  },
  // used for strain, recovery and sleep
  setSelectedDateRange(state, dtRange) {
    localStorage.setItem("selectedDateRange", dtRange);
  },
  setUsersToday(state, usersToday) {
    state.usersToday = usersToday;
  },
  setDataSleepRanges(state, dataSleepRanges) {
    state.dataSleepRanges = dataSleepRanges;
  },
  setDataSleep(state, dataSleep) {
    state.dataSleep = dataSleep;
  },
  setDataSleepHours(state, dataSleepHours) {
    state.dataSleepHours = dataSleepHours;
  },
  setDataRecoveryRanges(state, dataRecoveryRanges) {
    state.dataRecoveryRanges = dataRecoveryRanges;
  },
  setDataRecovery(state, dataRecovery) {
    state.dataRecovery = dataRecovery;
  },
  setDataAvgRecoveryAndHrv(state, dataAvgRecoveryAndHrv) {
    state.dataAvgRecoveryAndHrv = dataAvgRecoveryAndHrv;
  },
  setDataStrainRanges(state, dataStrainRanges) {
    state.dataStrainRanges = dataStrainRanges;
  },
  setDataStrain(state, dataStrain) {
    state.dataStrain = dataStrain;
  },
  setDataStrainAvgMaxHr(state, dataStrainAvgMaxHr) {
    state.dataStrainAvgMaxHr = dataStrainAvgMaxHr;
  },
  setDataWorkoutTypes(state, dataWorkoutTypes) {
    state.dataWorkoutTypes = dataWorkoutTypes;
  },
  setDataWorkouts(state, dataWorkouts) {
    state.dataWorkouts = dataWorkouts;
  },
  setUserDnaProfiles(state, dnaProfiles) {
    state.userDnaProfiles = dnaProfiles;
  },
};

export const actions = {
  /***
   * Global
   */
  selectedDate({ commit }, { dt }) {
    commit("setSelectedDate", dt);
  },
  selectedDateRange({ commit }, { dtRange }) {
    commit("setSelectedDateRange", dtRange);
  },
  /***
   * Users
   * Today's strain, recovery, sleep socres, workouts, device battery level and last sync timestamp
   */
  today(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      search_term,
      dt_at = dayjs().utc().format(Constants.BUCKET_FORMAT),
      battery, 
      sync_from, 
      sync_to,
      platoon_id,
    }
  ) {
    return DataService.today({
      page,
      page_size,
      sort,
      order,
      section_id,
      search_term,
      dt_at,
      battery, 
      sync_from, 
      sync_to,
      platoon_id,
    })
      .then((res) => {
        commit("setUsersToday", res.data);
        commit("setSelectedDate", dt_at);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  /***
   * Sleep
   */
  sleepRangesBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.sleepRangesBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataSleepRanges", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  sleepDataBySection(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.sleepDataBySection({
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataSleep", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  sleepHoursBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.sleepHoursBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataSleepHours", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  /***
   * Recovery
   */
  recoveryRangesBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.recoveryRangesBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataRecoveryRanges", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  recoveryDataBySection(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.recoveryDataBySection({
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataRecovery", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  avgRecoveryAndHrvBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.avgRecoveryAndHrvBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataAvgRecoveryAndHrv", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  /***
   * Strain
   */
  strainRangesBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.strainRangesBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataStrainRanges", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  strainDataBySection(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.strainDataBySection({
      page,
      page_size,
      sort,
      order,
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataStrain", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  strainAvgMaxHrBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
      platoon_id,
    }
  ) {
    return DataService.strainAvgMaxHrBySection({
      section_id,
      dt_from,
      dt_to,
      platoon_id,
    })
      .then((res) => {
        commit("setDataStrainAvgMaxHr", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  strainWorkoutTypesBySection(
    { commit, dispatch },
    {
      section_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.strainWorkoutTypesBySection({
      section_id,
      dt_from,
      dt_to,
    })
      .then((res) => {
        commit("setDataWorkoutTypes", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  strainWorkoutsBySection(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      workout_type,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.strainWorkoutsBySection({
      page,
      page_size,
      sort,
      order,
      section_id,
      workout_type,
      dt_from,
      dt_to,
    })
      .then((res) => {
        commit("setDataWorkouts", res.data);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  /** Dashboard */
  scoreRanges(
    { dispatch },
    {
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.scoreRanges({
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  performanceByPlatoonAndSection(
    { commit, dispatch },
    {
      page,
      page_size,
      sort,
      order,
      section_id,
      platoon_id,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.performanceByPlatoonAndSection({
      page,
      page_size,
      sort,
      order,
      section_id,
      platoon_id,
      dt_from,
      dt_to,
    })
      .then((res) => {
        commit("setSelectedDate", dt_from);
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  /** User Physiological Data */
  userDataByDate({ getters, dispatch }, { udid, dt = null }) {
    let dtFilter = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT); // today

    if (!dt) {
      // use global selectedDate
      let selectedDate = getters.getSelectedDate();
      if (selectedDate) dtFilter = selectedDate;
    } else dtFilter = dayjs(dt).format(Constants.BUCKET_FORMAT);

    return DataService.userDataByDate({
      udid,
      dt: dtFilter,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userDna({ dispatch }, { udid }) {
    return DataService.userDna({
      udid,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        // user does not have DNA data
        if (e.response.status == StatusCodes.NOT_FOUND) {
          return null;
        }

        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userStrainData({ dispatch }, { udid, dt_from, dt_to }) {
    return DataService.userStrainData({
      udid,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userWorkoutTypes({ dispatch }, { udid, dt_from, dt_to }) {
    return DataService.userWorkoutTypes({
      udid,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userWorkouts(
    { dispatch },
    {
      udid,
      workout_type,
      page,
      page_size,
      sort,
      order,
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.userWorkouts({
      udid,
      workout_type,
      page,
      page_size,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userRecoveryData({ dispatch }, { udid, dt_from, dt_to }) {
    return DataService.userRecoveryData({
      udid,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userSleepData({ dispatch }, { udid, sort, order, dt_from, dt_to }) {
    return DataService.userSleepData({
      udid,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  // Profiler
  userDnaProfiles({ dispatch }, { udid, dna_profile_id = null }) {
    return DataService.userDnaProfiles({
      udid,
      dna_profile_id,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        // user does not have DNA data
        if (e.response.status == StatusCodes.NOT_FOUND) {
          return null;
        }

        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  candidatesForProfileType(
    { dispatch },
    {
      dna_profile_id,
      sort,
      order,
      page,
      page_size,
      min_confidence,
      max_confidence,
      search_term,
      platoon_id,
      section_id,
    }
  ) {
    return DataService.candidatesForProfileType({
      dna_profile_id,
      sort,
      order,
      page,
      page_size,
      min_confidence,
      max_confidence,
      search_term,
      platoon_id,
      section_id,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  abnormalAlerts(
    { dispatch },
    {
      dt_from = dayjs().utc().startOf("day").format(Constants.BUCKET_FORMAT),
      dt_to = dayjs().utc().endOf("day").format(Constants.BUCKET_FORMAT),
    }
  ) {
    return DataService.getAbnormalAlerts({dt_from, dt_to})
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userStressData({ dispatch }, { udid, sort, order, dt_from, dt_to }) {
    return DataService.userStressData({
      udid,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userPerformanceData({ dispatch }, { udid, sort, order, dt_from, dt_to }) {
    return DataService.userPerformanceData({
      udid,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userStressAndSpoData({ dispatch }, { udid, sort, order, dt_from, dt_to }) {
    return DataService.userStressAndSpoData({
      udid,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userWorkoutDetails({ dispatch }, { udid, workout_record_id }) {
    return DataService.userWorkoutDetails({
      udid,
      workout_record_id,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userSleepDetails({ dispatch }, { udid, sort, order, dt_from, dt_to }) {
    return DataService.userSleepDetails({
      udid,
      sort,
      order,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userWellnessDetails({ dispatch }, { udid, selected_date, section_id }) {
    return DataService.userWellnessDetails({ udid, selected_date, section_id })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  downloadUserProfile({ dispatch }, { udid, dt_from, dt_to }) {
    return DataService.downloadUserProfile({
      udid,
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  userPerformanceTrends({ dispatch }, { dt_from, dt_to }) {
    return DataService.userPerformanceTrends({
      dt_from,
      dt_to,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
  topPerformingUsers({ dispatch }, { dt_from, dt_to, days, platoon_id, search_term, page_num }) {
    return DataService.topPerformingUsers({
      dt_from,
      dt_to,
      days,
      platoon_id,
      search_term,
      page_num,
    })
      .then((res) => {
        return res.data;
      })
      .catch((e) => {
        dispatch("setError", e, { root: true });
        return Promise.reject(e);
      });
  },
};

export const getters = {
  getSelectedDate: () => () => {
    return localStorage.getItem("selectedDate");
  },
};
